import React, {useMemo, useState} from "react";
import Link from "@components/elements/DocumentLink";
import Image from "next/image";
import {twMerge} from "tailwind-merge";
import {IProcessedPluginData} from "@hooks/plugins/IProcessedPluginsData";
import {StarRating} from "@components/elements/StarRating";
import {SelectField} from "@components/elements/SelectField";
import {TableSortingOption} from "@components/elements/tables/TableSortingOption";
import {getPluginMaintenanceInsights} from "@utils/pluginInsights";
import {FaDownload, FaHistory, FaPlug, FaUsers} from "react-icons/fa";
import {formatNumber} from "@utils/format";
import {useStableNow} from "@hooks/useStableNow";

type SortField =
    | "active_installs"
    | "downloaded"
    | "rating"
    | "num_ratings"
    | "last_updated"
    | "maintenance";

const SORT_FIELD_OPTIONS = [
    new TableSortingOption({label: "Active installs", descending: false, slug: "active_installs"}),
    new TableSortingOption({label: "Downloads", descending: false, slug: "downloaded"}),
    new TableSortingOption({label: "Rating", descending: false, slug: "rating"}),
    new TableSortingOption({label: "Reviews", descending: false, slug: "num_ratings"}),
    new TableSortingOption({label: "Recently updated", descending: false, slug: "last_updated"}),
    new TableSortingOption({label: "Maintenance score", descending: false, slug: "maintenance"}),
];

const SORT_ORDER_OPTIONS = [
    new TableSortingOption({label: "Descending", descending: true, slug: "desc"}),
    new TableSortingOption({label: "Ascending", descending: false, slug: "asc"}),
];

const getDaysSince = (date: Date, nowMs?: number | null): number | null => {
    if (!nowMs) return null;
    const diffMs = nowMs - date.getTime();
    if (!Number.isFinite(diffMs)) return null;
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
};

const maintenanceLabel: Record<string, string> = {
    "actively-maintained": "Actively maintained",
    "maintained": "Maintained",
    "stale": "Stale",
    "possibly-abandoned": "Possibly abandoned",
};

export function PluginsSortableList(props: {
    plugins: IProcessedPluginData[];
    className?: string;
    compareBasePluginId?: string;
    nowTimestamp?: number | null;
}) {
    const now = useStableNow(props.nowTimestamp);
    const [sortField, setSortField] = useState<SortField>(() => SORT_FIELD_OPTIONS[0].getSlug() as SortField);
    const [sortDescending, setSortDescending] = useState<boolean>(true);

    const maintenanceById = useMemo(() => {
        const map = new Map<string, ReturnType<typeof getPluginMaintenanceInsights>>();
        for (const plugin of props.plugins) {
            map.set(plugin.pluginid, getPluginMaintenanceInsights(plugin, now ?? undefined));
        }
        return map;
    }, [props.plugins, now]);

    const sortedPlugins = useMemo(() => {
        const plugins = [...props.plugins];

        const sortValue = (plugin: IProcessedPluginData): number => {
            switch (sortField) {
                case "active_installs":
                    return plugin.active_installs ?? 0;
                case "downloaded":
                    return plugin.downloaded ?? 0;
                case "rating":
                    return plugin.rating ?? 0;
                case "num_ratings":
                    return plugin.num_ratings ?? 0;
                case "last_updated":
                    return plugin.last_updated?.getTime?.() ?? 0;
                case "maintenance":
                    return maintenanceById.get(plugin.pluginid)?.score ?? 0;
                default:
                    return plugin.active_installs ?? 0;
            }
        };

        plugins.sort((a, b) => {
            const delta = sortValue(a) - sortValue(b);
            return sortDescending ? -delta : delta;
        });
        return plugins;
    }, [props.plugins, sortField, sortDescending, maintenanceById]);

    const selectedFieldOption = useMemo(
        () => SORT_FIELD_OPTIONS.find((option) => option.getSlug() === sortField) ?? SORT_FIELD_OPTIONS[0],
        [sortField]
    );
    const selectedOrderOption = useMemo(
        () => SORT_ORDER_OPTIONS.find((option) => option.getDescending() === sortDescending) ?? SORT_ORDER_OPTIONS[0],
        [sortDescending]
    );

    return (
        <div className={twMerge("flex flex-col gap-4", props.className)}>
            <div className={twMerge("flex flex-col md:flex-row md:items-center md:justify-between gap-3")}>
                <div className={twMerge("text-sm text-gray-600 dark:text-gray-400")}>
                    Showing {formatNumber(props.plugins.length)} plugins
                </div>
                <div className={twMerge("flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center")}>
                    <div className={twMerge("w-full md:w-[240px] min-w-0")}>
                        <SelectField
                            id="hub-sort-field"
                            className="w-full"
                            placeholder="Sort field"
                            value={selectedFieldOption}
                            options={SORT_FIELD_OPTIONS}
                            onChange={(option) => setSortField(option.getSlug() as SortField)}
                        />
                    </div>
                    <div className={twMerge("w-full md:w-[200px] min-w-0")}>
                        <SelectField
                            id="hub-sort-order"
                            className="w-full"
                            placeholder="Order"
                            value={selectedOrderOption}
                            options={SORT_ORDER_OPTIONS}
                            onChange={(option) => setSortDescending(option.getDescending())}
                        />
                    </div>
                </div>
            </div>

            <div className={twMerge("flex flex-col gap-3")}>
                {sortedPlugins.map((plugin) => {
                    const maintenance = maintenanceById.get(plugin.pluginid);
                    const updatedDaysAgo = getDaysSince(plugin.last_updated, now);
                    const updatedLabel = updatedDaysAgo === null ? "Updated —" : `Updated ${updatedDaysAgo}d ago`;
                    const maintenanceText = maintenance ? `${maintenance.score}/100 • ${maintenanceLabel[maintenance.verdict]}` : "—";
                    const description = plugin.short_description || plugin.htmlStrippedDescription || "";
                    const marketplaceLabel = plugin.marketplace === 'woocommerce' ? 'WooCommerce Marketplace' : 'WordPress.org';

                    return (
                        <div
                            key={plugin.pluginid}
                            className={twMerge(
                                "group rounded-2xl border border-gray-200 dark:border-gray-700",
                                "bg-white/80 dark:bg-gray-900/40",
                                "p-4 shadow-sm hover:shadow-md transition-shadow",
                                "flex flex-col gap-3",
                            )}
                        >
                            <div className="flex items-start gap-4">
                                <div
                                    className={twMerge(
                                        "relative flex-shrink-0",
                                        "w-12 h-12 md:w-14 md:h-14",
                                        "overflow-hidden rounded-xl",
                                        "border border-gray-200 dark:border-gray-700",
                                        "bg-gray-100 dark:bg-gray-800",
                                    )}
                                    title={plugin.name}
                                >
                                    {plugin.icons?.length ? (
                                        <Image
                                            src={plugin.icons[0].url}
                                            alt={plugin.name}
                                            fill
                                            className="object-contain"
                                            unoptimized={true}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <FaPlug className="w-7 h-7 text-gray-400 dark:text-gray-500" aria-hidden="true"/>
                                        </div>
                                    )}
                                </div>

                                <div className="min-w-0 flex-1 flex flex-col gap-3">
                                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                        <div className="min-w-0">
                                            <Link
                                                href={`/plugins/${plugin.pluginid}`}
                                                className={twMerge("text-lg font-semibold truncate group-hover:text-indigo-700 dark:group-hover:text-indigo-300 hover:underline")}
                                            >
                                                {plugin.name}
                                            </Link>
                                            {description && (
                                                <div className={twMerge("mt-1 text-sm text-gray-700 dark:text-gray-300 line-clamp-2")}>
                                                    {description}
                                                </div>
                                            )}
                                            <div className={twMerge("mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-gray-400")}>
                                                <span className="inline-flex items-center gap-1">
                                                    <StarRating rating={plugin.rating} maxRating={5} starSizeClassName="w-4 h-4"/>
                                                    <span className="text-gray-500 dark:text-gray-400">({formatNumber(plugin.num_ratings)})</span>
                                                </span>
                                                <span className={twMerge("hidden sm:inline")}>•</span>
                                                <span className="inline-flex items-center gap-1">
                                                    <FaHistory className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                    {updatedLabel}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-2 text-xs sm:flex-row sm:flex-wrap sm:items-center">
                                            <span className="inline-flex w-full max-w-full items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200 sm:w-auto">
                                                <FaPlug className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                {formatNumber(plugin.active_installs)} installs
                                            </span>
                                            <span className="inline-flex w-full max-w-full items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200 sm:w-auto">
                                                <FaDownload className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                {formatNumber(plugin.downloaded ?? 0)} downloads
                                            </span>
                                            <span className="inline-flex w-full max-w-full items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200 sm:w-auto">
                                                <FaUsers className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                {maintenanceText}
                                            </span>
                                        </div>
                                    </div>

                                    <div className={twMerge("flex flex-row flex-wrap items-center gap-2")}>
                                        {props.compareBasePluginId && props.compareBasePluginId !== plugin.pluginid && (
                                            <Link
                                                href={`/compare/${props.compareBasePluginId}-vs-${plugin.pluginid}`}
                                                className="inline-flex min-w-0 max-w-full items-center justify-center rounded-full bg-indigo-600 px-3 py-1.5 text-center text-xs font-semibold leading-tight text-white shadow-sm hover:bg-indigo-700 whitespace-normal"
                                            >
                                                Compare
                                            </Link>
                                        )}
                                        <Link
                                            href={`/alternatives-to/${plugin.pluginid}`}
                                            className="inline-flex min-w-0 max-w-full items-center justify-center rounded-full border border-gray-200 bg-white px-3 py-1.5 text-center text-xs font-semibold leading-tight text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 whitespace-normal"
                                        >
                                            Alternatives
                                        </Link>
                                        <a
                                            href={plugin.wordpress_link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex min-w-0 max-w-full items-center justify-center rounded-full border border-gray-200 bg-white px-3 py-1.5 text-center text-xs font-semibold leading-tight text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 whitespace-normal"
                                        >
                                            {marketplaceLabel}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
